import { Col, Table } from "antd";
import { useRef, useState } from "react";
import ChartWithPDF from "./chartwithpdf";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import moment from "moment";

const AuditCount = ({
  chartFor,
  chartKey,
  slsRole,
  slsSelectedMonthRange,
  dashboardDetails,
  barBackgroundColor,
  marginTop,
  fileName,
}) => {
  const gradientRef = useRef(null);
  const hoveredIndexRef = useRef(null);

  const initGradient = (chart) => {
    if (gradientRef.current) return;

    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const gradient = ctx.createLinearGradient(
      0,
      chartArea.bottom,
      0,
      chartArea.top,
    );

    gradient.addColorStop(0, "#EDEDED");
    gradient.addColorStop(0.95, "#112C7D");

    gradientRef.current = gradient;
  };

  // New Audit Count Chart Data
  const barCount =
    dashboardDetails?.auditCounts?.[chartFor]?.labels?.length || 0;

  const isManyBars = barCount > 15;

  const barSizing = isManyBars
    ? {
        barPercentage: 0.75,
        categoryPercentage: 0.8,
      }
    : {
        barThickness: slsRole === process.env.REACT_APP_AROLE ? 33 : 22,
        maxBarThickness: slsRole === process.env.REACT_APP_AROLE ? 33 : 22,
      };

  //const useFixedWidth = barCount <= 8;
  const auditCountChartdata = {
    labels: dashboardDetails?.auditCounts?.[chartFor]?.labels,
    datasets: [
      {
        label: slsSelectedMonthRange,
        data: dashboardDetails?.auditCounts?.[chartFor]?.values,

        // barThickness: 43,

        // FORCE all borders
        borderSkipped: false,

        // Rounded corners
        borderRadius: 5,
        backgroundColor: barBackgroundColor, // normal
        hoverBackgroundColor: barBackgroundColor, // overwritten later

        // barPercentage: 0.75,
        // categoryPercentage: 0.8,
        ...barSizing,
        //}),
      },
    ],
  };

  const auditCountChartoptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    // begin:Remove graph grid
    scales: {
      x: {
        display: true, //Client name coming at the down
        grid: {
          display: false, // remove vertical grid
          drawBorder: false, // remove x-axis line
        },
        ticks: {
          display: true,
          color: (context) => {
            return context.index === hoveredIndexRef.current
              ? "#000000" // highlighted color
              : "#000000"; // normal color
          },
          font: (context) => {
            return context.index === hoveredIndexRef.current
              ? { weight: "normal", size: 11 }
              : { weight: "normal", size: 11 };
          },
        },
      },
      y: {
        display: false,
        min: 0,
        suggestedMax:
          Math.max(
            ...(dashboardDetails?.auditCounts?.[chartFor]?.values || []),
          ) * 1.2,
        grid: {
          display: false, //  remove horizontal grid
          drawBorder: false, //  remove y-axis line
        },
        ticks: {
          display: true,
        },
      },
    },
    // end:Remove graph grid
    onHover: (event, elements, chart) => {
      if (!elements.length) {
        hoveredIndexRef.current = null;
        chart.data.datasets[0].hoverBackgroundColor = barBackgroundColor;
        chart.update("none"); //  no animation
        return;
      }

      const index = elements[0].index;
      hoveredIndexRef.current = index;

      initGradient(chart);

      chart.data.datasets[0].hoverBackgroundColor = "#BBCCFD";
      //chart.data.datasets[0].hoverBackgroundColor = gradientRef.current;
      chart.update("none"); //  no animation
    },

    plugins: {
      legend: {
        display: false, //  hides legend completely
      },
      datalabels: {
        display: true, //Labels count are coming
        color: "black",
        anchor: "end",
        align: "top",
        // anchor: (context) => {
        //   const value = context.dataset.data[context.dataIndex];
        //   const maxValue = Math.max(...context.dataset.data);

        //   // If bar is tall → keep label inside
        //   return value > maxValue * 0.8 ? "center" : "end";
        // },

        // align: (context) => {
        //   const value = context.dataset.data[context.dataIndex];
        //   const maxValue = Math.max(...context.dataset.data);

        //   return value > maxValue * 0.8 ? "center" : "top";
        // },
        clamp: true,
        clip: false,
        font: {
          weight: "bold",
          size: "10px",
        },
        formatter: (value, context) => {
          const month = context.chart.data.labels[context.dataIndex];
          const year = moment.utc(new Date()).local().format("YYYY");
          return `${value}`;
        },
        backgroundColor: "#f8f8f800",
        color: "#000000",
        borderRadius: "5", // Add border radius
        padding: "3",
      },
      tooltip: {
        enabled: false,
        external: function (context) {
          const { chart, tooltip } = context;

          // Attach tooltip to chart parent
          let tooltipEl =
            chart.canvas.parentNode.querySelector(".custom-tooltip");

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.className = "custom-tooltip";
            chart.canvas.parentNode.appendChild(tooltipEl);
          }

          // Hide tooltip
          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          const title = tooltip.title?.[0] || "";
          const value = tooltip.dataPoints?.[0]?.raw ?? 0;

          tooltipEl.innerHTML = `
      <div class="tooltip-title">${title}</div>
      <div class="tooltip-value">
        Count:
        <span>${Number(value).toLocaleString("en-US", {
          minimumFractionDigits: 2,
        })}</span>
      </div>
    `;

          // ─────────────────────────────
          // 🎯 Position tooltip at TOP of bar
          // ─────────────────────────────
          const dataPoint = tooltip.dataPoints[0];
          const element = dataPoint.element;

          const { x, y } = element.tooltipPosition();

          const tooltipWidth = tooltipEl.offsetWidth;
          const tooltipHeight = tooltipEl.offsetHeight;

          tooltipEl.style.opacity = 1;
          tooltipEl.style.position = "absolute";

          // Center horizontally
          tooltipEl.style.left = `${x - tooltipWidth / 2}px`;

          // Move ABOVE the bar
          tooltipEl.style.top = `${y - tooltipHeight - 8}px`;

          tooltipEl.style.pointerEvents = "none";
        },
      },
    },
  };
  const [showAuditCountDetails, setShowAuditCountDetails] = useState(false);
  const downloadExcel = () => {
    // Excel-only columns (not displayed in the frontend table)
    const excelOnlyFields = {
      Department: "departments",
      "Emp ID": "empId",
    };

    const flattenData = (data, parent = {}) => {
      const rows = [];

      data.forEach((row) => {
        // Add the current row
        const flatRow = {
          ...parent, // Include parent details (if any)
          // First two columns in Excel
          Department: row.departments ?? "N/A",
          "Emp ID": row.empId ?? "N/A",

          ...columns.reduce((acc, col) => {
    if (col.dataIndex === "auditScore") {
      const score = parseFloat(row.auditScore);
      acc[col.title] = Number.isFinite(score) ? score / 100 : null;
    } else {
      acc[col.title] = row[col.dataIndex];
    }

    return acc;
  }, {}),
          
        };
        rows.push(flatRow);

        // Add children (if any)
        if (row.children && row.children.length > 0) {
          const childRows = flattenData(
            row.children,
            slsRole === process.env.REACT_APP_AUDROLE ||
              slsRole === process.env.REACT_APP_TLROLE ||
              slsRole === process.env.REACT_APP_ATLROLE ||
              slsRole === process.env.REACT_APP_QC
              ? {
                  "Parent AuditForm": row.auditForm || "N/A",
                }
              : {
                  "Parent Client/TL": row.client || row.tl || "N/A",
                },
          );
          rows.push(...childRows);
        }
      });

      return rows;
    };

    // Flatten the dashboard details
    const data = flattenData(
      dashboardDetails?.auditCountDetails?.[chartFor] || [],
    );

    // Generate Excel
    const worksheet = XLSX.utils.json_to_sheet(data);
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

for (let row = 1; row <= range.e.r; row++) {
  const cellAddress = XLSX.utils.encode_cell({
    r: row,
    c: Object.keys(data[0]).indexOf("Audit Score"),
  });

  if (worksheet[cellAddress]) {
    worksheet[cellAddress].z = "0.00%";
  }
}
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${fileName}_Details.xlsx`);
  };

  const additionalColumns =
    slsRole === process.env.REACT_APP_MGROLE
      ? [
          {
            title: "TL",
            dataIndex: "tl",
            render: (text, record) => {
              const tls =
                text && typeof text === "string" && text?.includes(",")
                  ? text?.split(",").length
                  : text;
              return <div>{tls}</div>;
            },
          },
        ]
      : [];

  const isAgentAuditCount =
    chartFor ===
    process.env.REACT_APP_AGENT_AUDIT_COUNT_FILENAME.replaceAll("_", " ");

  const columns = [
    ...(slsRole !== process.env.REACT_APP_AUDROLE &&
    slsRole !== process.env.REACT_APP_TLROLE &&
    slsRole !== process.env.REACT_APP_ATLROLE &&
    slsRole !== process.env.REACT_APP_QC &&
    !isAgentAuditCount &&
    slsRole === process.env.REACT_APP_AMROLE
      ? [
          {
            title: "Client",
            dataIndex: "client",
          },
        ]
      : []),
    ...((slsRole === process.env.REACT_APP_AUDROLE ||
      slsRole === process.env.REACT_APP_TLROLE ||
      slsRole === process.env.REACT_APP_ATLROLE ||
      slsRole === process.env.REACT_APP_QC) &&
    chartFor !==
      process.env.REACT_APP_AGENT_AUDIT_COUNT_FILENAME.replaceAll("_", " ")
      ? [
          {
            title: "Audit Form",
            dataIndex: "auditForm",
          },
        ]
      : []),

    ...additionalColumns,
    ...(slsRole !== process.env.REACT_APP_AUDROLE &&
    slsRole !== process.env.REACT_APP_TLROLE &&
    slsRole !== process.env.REACT_APP_ATLROLE &&
    slsRole !== process.env.REACT_APP_QC &&
    !isAgentAuditCount &&
    slsRole === process.env.REACT_APP_AMROLE
      ? [
          {
            title: "Department",
            dataIndex: "departments",
          },
        ]
      : []),
    {
      title: "Agents",
      dataIndex: "agents",
      render: (text, record) => {
        const agents =
          text && typeof text === "string" && text?.includes(",")
            ? text?.split(",").length
            : text;
        return <div>{agents}</div>;
      },
    },
    ...(slsRole === process.env.REACT_APP_AUDROLE ||
    slsRole === process.env.REACT_APP_TLROLE ||
    slsRole === process.env.REACT_APP_ATLROLE ||
    slsRole === process.env.REACT_APP_QC ||
    (isAgentAuditCount && slsRole === process.env.REACT_APP_AMROLE)
      ? [
          {
            title: "Audit Count",
            dataIndex: "auditCount",
          },
        ]
      : []),
    {
      title: "Audit Score",
      dataIndex: "auditScore",
    },
  ];
  return (
    <Col xs={24} md={slsRole === process.env.REACT_APP_AROLE ? 16 : 24}>
      <div className={`${marginTop} card px-3 py-4 auditCount-container`}>
        <div className="auditCount-container-header">
          <p className="dsh-chart-p m-0">
            {chartFor}
            {/* ({slsSelectedMonthRange})<br /> */}
            {/* {dashboardDetails?.auditCount} */}
          </p>
          <div className="d-flex align-items-center gp-10">
            <p className="dsh-chart-p m-0 vDetails">
              <div
                className={
                  showAuditCountDetails ? "triangle-up" : "triangle-down"
                }
              ></div>
              <span
                onClick={() => {
                  showAuditCountDetails === false
                    ? setShowAuditCountDetails(true)
                    : setShowAuditCountDetails(false);
                }}
              >
                View Details
              </span>
            </p>
          </div>
        </div>
        <ChartWithPDF
          key={chartKey}
          type="bar"
          resHeight="400"
          data={auditCountChartdata}
          options={auditCountChartoptions}
          fileName={fileName}
          customClass={showAuditCountDetails ? "cwac" : ""}
          chartFor="Audit Count"
        />

        <div className="auditCountTable-outer-container">
          {showAuditCountDetails && (
            <>
              <button
                className="auditCountBarChart-dsh-download-excel border border-light-dark btn font-size-md font-weight-bold font-weight-bolder text-dark-custom"
                onClick={downloadExcel}
              >
                Download as Excel
              </button>
              <Table
                className={
                  slsRole === process.env.REACT_APP_AROLE ||
                  slsRole === process.env.REACT_APP_QC
                    ? "mt-3 rounded dsh-table"
                    : "mt-3 rounded dsh-table w-100"
                }
                dataSource={dashboardDetails?.auditCountDetails?.[chartFor]}
                columns={columns}
                scroll={{ x: 1000 }}
                pagination={false} // Pagination settings
              />
            </>
          )}
        </div>
      </div>
    </Col>
  );
};

export default AuditCount;
